import { makeCrud, makeCrudHooks } from './lib/crud';

// One entry per flat CRUD resource. Each: { key, base, title, singular, columns, fields }.
// hooks are attached lazily below. Places & itineraries live in their own folders (detail views).
const yesNo = (v) => (v ? 'Yes' : 'No');

const DEFS = [
  {
    key: 'food-preferences', base: '/food-preferences', title: 'Food Preferences', singular: 'Food Preference',
    columns: [
      { key: 'name', header: 'Name' },
      { key: 'icon', header: 'Icon' },
      { key: 'displayOrder', header: 'Order' },
    ],
    fields: [
      { name: 'name', label: 'Name', required: true },
      { name: 'icon', label: 'Icon', placeholder: 'icon name or URL' },
      { name: 'displayOrder', label: 'Display order', type: 'number' },
    ],
  },
  {
    key: 'group-types', base: '/group-types', title: 'Group Types', singular: 'Group Type',
    columns: [
      { key: 'name', header: 'Name' },
      { key: 'icon', header: 'Icon' },
      { key: 'displayOrder', header: 'Order' },
    ],
    fields: [
      { name: 'name', label: 'Name', required: true },
      { name: 'icon', label: 'Icon', placeholder: 'icon name or URL' },
      { name: 'displayOrder', label: 'Display order', type: 'number' },
    ],
  },
  {
    key: 'food-types', base: '/food-types', title: 'Food Types', singular: 'Food Type',
    columns: [
      { key: 'name', header: 'Name' },
      { key: 'icon', header: 'Icon' },
      { key: 'displayOrder', header: 'Order' },
    ],
    fields: [
      { name: 'name', label: 'Name', required: true },
      { name: 'icon', label: 'Icon', placeholder: 'icon name or URL' },
      { name: 'displayOrder', label: 'Display order', type: 'number' },
    ],
  },
  {
    key: 'trip-types', base: '/trip-types', title: 'Trip Types', singular: 'Trip Type',
    columns: [
      { key: 'name', header: 'Name' },
      { key: 'icon', header: 'Icon' },
      { key: 'displayOrder', header: 'Order' },
    ],
    fields: [
      { name: 'name', label: 'Name', required: true },
      { name: 'icon', label: 'Icon', placeholder: 'icon name or URL' },
      { name: 'displayOrder', label: 'Display order', type: 'number' },
    ],
  },
  {
    key: 'accommodation-preferences', base: '/accommodation-preferences', title: 'Accommodation Preferences', singular: 'Accommodation Preference',
    columns: [
      { key: 'name', header: 'Name' },
      { key: 'icon', header: 'Icon' },
      { key: 'displayOrder', header: 'Order' },
    ],
    fields: [
      { name: 'name', label: 'Name', required: true },
      { name: 'icon', label: 'Icon', placeholder: 'icon name or URL' },
      { name: 'displayOrder', label: 'Display order', type: 'number' },
    ],
  },
  {
    key: 'states', base: '/states', title: 'States', singular: 'State',
    columns: [
      { key: 'name', header: 'Name' },
      { key: 'code', header: 'Code' },
      { key: 'isPopular', header: 'Popular', render: (r) => yesNo(r.isPopular) },
      { key: 'displayOrder', header: 'Order' },
    ],
    fields: [
      { name: 'name', label: 'Name', required: true },
      { name: 'code', label: 'Code', placeholder: 'e.g. KA' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'imageUrl', label: 'Image URL' },
      { name: 'isPopular', label: 'Popular', type: 'checkbox' },
      { name: 'displayOrder', label: 'Display order', type: 'number' },
    ],
  },
  {
    key: 'tabs', base: '/tabs', title: 'Tabs', singular: 'Tab',
    columns: [
      { key: 'label', header: 'Label' },
      { key: 'route', header: 'Route' },
      { key: 'isEnabled', header: 'Enabled', render: (r) => yesNo(r.isEnabled) },
      { key: 'displayOrder', header: 'Order' },
    ],
    fields: [
      { name: 'label', label: 'Label', required: true },
      { name: 'icon', label: 'Icon' },
      { name: 'route', label: 'Route', required: true, placeholder: '/home' },
      { name: 'displayOrder', label: 'Display order', type: 'number' },
      { name: 'isEnabled', label: 'Enabled', type: 'checkbox' },
    ],
  },
  {
    key: 'pages', base: '/pages', title: 'Pages', singular: 'Page',
    columns: [
      { key: 'title', header: 'Title' },
      { key: 'slug', header: 'Slug' },
      { key: 'isPublished', header: 'Published', render: (r) => yesNo(r.isPublished) },
    ],
    fields: [
      { name: 'title', label: 'Title', required: true },
      { name: 'slug', label: 'Slug', required: true, placeholder: 'about-us' },
      { name: 'body', label: 'Body (HTML)', type: 'textarea' },
      { name: 'isPublished', label: 'Published', type: 'checkbox' },
    ],
  },
  {
    key: 'ads', base: '/ads', title: 'Ads', singular: 'Ad',
    columns: [
      { key: 'title', header: 'Title' },
      { key: 'placement', header: 'Placement' },
      { key: 'isActive', header: 'Active', render: (r) => yesNo(r.isActive) },
    ],
    fields: [
      { name: 'title', label: 'Title', required: true },
      { name: 'imageUrl', label: 'Image URL' },
      { name: 'targetUrl', label: 'Target URL' },
      { name: 'placement', label: 'Placement', type: 'select', options: ['home', 'list', 'detail'] },
      { name: 'startDate', label: 'Start date', type: 'date' },
      { name: 'endDate', label: 'End date', type: 'date' },
      { name: 'isActive', label: 'Active', type: 'checkbox' },
    ],
  },
  {
    key: 'activities', base: '/activities', title: 'Activities', singular: 'Activity',
    columns: [
      { key: 'name', header: 'Name' },
      { key: 'category', header: 'Category' },
      { key: 'duration', header: 'Duration' },
      { key: 'price', header: 'Price' },
    ],
    fields: [
      { name: 'name', label: 'Name', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'placeId', label: 'Place ID', placeholder: 'related place id' },
      { name: 'category', label: 'Category' },
      { name: 'duration', label: 'Duration', placeholder: 'e.g. 2h' },
      { name: 'price', label: 'Price', type: 'number' },
      { name: 'imageUrl', label: 'Image URL' },
    ],
  },
  {
    key: 'users', base: '/users', title: 'Users', singular: 'User',
    columns: [
      { key: 'name', header: 'Name' },
      { key: 'email', header: 'Email' },
      { key: 'role', header: 'Role' },
      { key: 'status', header: 'Status' },
    ],
    fields: [
      { name: 'name', label: 'Name', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'phone', label: 'Phone' },
      { name: 'role', label: 'Role', type: 'select', options: ['admin', 'user'] },
      { name: 'status', label: 'Status', type: 'select', options: ['active', 'blocked'] },
      { name: 'avatarUrl', label: 'Avatar URL' },
    ],
  },
  {
    key: 'admins', base: '/admins', title: 'Admins', singular: 'Admin',
    canEdit: false, // API supports list + add + delete only (super admin only)
    columns: [
      { key: 'name', header: 'Name' },
      { key: 'email', header: 'Email' },
      { key: 'role', header: 'Role', render: (r) => <span className="badge">{r.role || '—'}</span> },
    ],
    fields: [
      { name: 'name', label: 'Name', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'role', label: 'Role', type: 'select', options: ['admin', 'super_admin'] },
    ],
  },
];

// Attach crud + hooks to every def and index by key.
export const CONFIGS = {};
DEFS.forEach((d) => {
  const crud = makeCrud(d.base);
  CONFIGS[d.key] = { ...d, crud, hooks: makeCrudHooks(d.key, crud) };
});
